/** Onboarding (S10 to S19), facts (S12) and the domain checker (S14). docs/41, docs/50 §4.1, docs/52 §3.2. */
import { z } from 'zod';
import { Day, Domain, Email, HttpsUrl, IsoDateTime, JsonValue, text } from '../common.ts';
import { DomainConnectStatus, FactSourceKind, FactStatus, OnboardingRunStatus, OnboardingStep, OnboardingStepState } from '../enums.ts';
import { ClockPauseReason } from '../golive.ts';
import { FactId, LocationId, UploadId, UserId } from '../ids.ts';
import { PhotoNeeds } from '../media.ts';

export const StepOwner = z.enum(['client', 'us']);

export const OnboardingStepView = z.object({
  step: OnboardingStep,
  locationId: LocationId.nullable(),
  state: OnboardingStepState,
  owner: StepOwner,
  dueAt: IsoDateTime.nullable(),
  doneAt: IsoDateTime.nullable(),
  doneBy: z.string().max(80).nullable(),
});

export const Onboarding = z.object({
  run: z.object({
    status: OnboardingRunStatus,
    startedAt: IsoDateTime,
    clockStartedAt: IsoDateTime.nullable(),
    deadlineAt: IsoDateTime.nullable(),
    liveAt: IsoDateTime.nullable(),
    /** Paused "waiting on you" time already added to `deadlineAt` (c041, c057). */
    waitingSeconds: z.number().int().min(0),
    /** The same as days to one decimal (`pausedDays` in golive.ts), for "The clock's paused 2 days". Added for c041. */
    pausedDays: z.number().min(0).optional(),
    /** Why the clock is paused right now: open `waiting_on_you` intervals that have started. Empty when running. */
    pausedFor: z.array(ClockPauseReason).max(5).optional(),
  }),
  steps: z.array(OnboardingStepView),
  weAreOnIt: z.array(z.object({ key: z.string().max(40), label: z.string().max(80), done: z.boolean() })),
  /** Signed, expiring preview link (docs/53 §7.6). */
  previewUrl: z.string().max(2048).nullable(),
  googleInviteAddress: Email.nullable(),
  photoAddress: Email.nullable(),
  canApproveGoLive: z.boolean(),
  approvers: z.array(z.object({ userId: UserId, name: z.string().max(80) })),
  bookingUrl: HttpsUrl.nullable(),
  correctionRound: z.object({ current: z.number().int().min(0), max: z.number().int().min(1) }),
  /** Where they stand against "enough photos to go live" (c057, k11): what's sent, approved and still missing. */
  photoNeeds: PhotoNeeds.optional(),
});

export const Registrar = z.enum(['godaddy', 'namecheap', 'squarespace', 'wix', 'cloudflare', 'other', 'none', 'web_person']);

const Pin = z.object({
  page: z.string().max(200),
  /** Element hint recorded by tap-to-comment, e.g. a CSS path. Treated as data only. */
  selector: z.string().max(300).optional(),
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  note: text(1000),
  voiceUploadId: UploadId.optional(),
});

/** One body per step (docs/41 steps). Unknown actions are rejected. */
export const OnboardingStepBody = z.discriminatedUnion('action', [
  z.object({ action: z.literal('google_invite_sent'), locationId: LocationId.optional() }).strict(),
  z
    .object({
      action: z.literal('domain_path'),
      domain: Domain,
      registrar: Registrar,
      designerEmail: Email.optional(),
    })
    .strict(),
  z.object({ action: z.literal('send_to_web_person'), designerEmail: Email }).strict(),
  z
    .object({
      action: z.literal('brand_check'),
      logoOk: z.boolean(),
      colorsOk: z.boolean(),
      pins: z.array(Pin).max(30),
    })
    .strict(),
  z.object({ action: z.literal('photos_done') }).strict(),
  z.object({ action: z.literal('licence_confirm'), correct: z.boolean(), note: text(500).optional() }).strict(),
  z.object({ action: z.literal('insurance_upload'), uploadId: UploadId, expiresOn: Day }).strict(),
  z.object({ action: z.literal('go_live_approve'), typedName: text(80, 2).optional() }).strict(),
  z.object({ action: z.literal('remind_approver'), userId: UserId }).strict(),
]);

export const OnboardingStepResponse = z.object({ step: OnboardingStepView, onboarding: Onboarding });

// ---------------------------------------------------------------------------------------------- facts

export const FactGroup = z.enum(['business', 'services', 'towns', 'hours', 'extras']);

export const FactView = z.object({
  id: FactId,
  path: z.string().max(120),
  label: z.string().max(80),
  value: JsonValue,
  status: FactStatus,
  source: z.object({ kind: FactSourceKind, ref: z.string().max(200), label: z.string().max(80) }),
  checkedAt: IsoDateTime,
  confirmation: z.enum(['pending', 'confirmed', 'edited', 'not_sure']),
  /** Whether a change re-renders the preview (docs/52 S12). */
  showsOnSite: z.boolean(),
});

export const Facts = z.object({ groups: z.array(z.object({ group: FactGroup, done: z.boolean(), facts: z.array(FactView) })) });

export const FactsConfirmBody = z
  .object({
    items: z
      .array(
        z.discriminatedUnion('decision', [
          z.object({ factId: FactId, decision: z.literal('confirm') }).strict(),
          z.object({ factId: FactId, decision: z.literal('edit'), value: JsonValue, note: text(500).optional() }).strict(),
          z.object({ factId: FactId, decision: z.literal('not_sure'), note: text(500).optional() }).strict(),
        ]),
      )
      .min(1)
      .max(100),
  })
  .strict();

export const FactsConfirmResponse = z.object({ updated: z.number().int().min(0), previewRebuilding: z.boolean() });

// ---------------------------------------------------------------------------------------------- domain check

export const DomainCheckBody = z
  .object({ domain: Domain })
  .strict();

export const DnsRecordCheck = z.object({
  type: z.enum(['A', 'AAAA', 'CNAME', 'TXT', 'MX']),
  name: z.string().max(253),
  expected: z.string().max(512),
  found: z.array(z.string().max(512)),
  ok: z.boolean(),
});

export const DomainCheck = z.object({
  domain: z.string().max(253),
  registrar: z.string().max(80).nullable(),
  dnsHost: z.string().max(80).nullable(),
  emailProvider: z.string().max(80).nullable(),
  status: DomainConnectStatus,
  records: z.array(DnsRecordCheck),
  mailCheckOk: z.boolean().nullable(),
  checkedAt: IsoDateTime,
});
